import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { navigationScroll } from '@/utils/navigation'
import { setDocumentTitle } from '@/utils/documentTitle'

const routes = [
  { path: '/', name: 'Home', component: () => import('@/views/Home.vue'), meta: { title: 'Home' } },
  { path: '/about', name: 'About', component: () => import('@/views/About.vue'), meta: { title: 'About' } },
  { path: '/courses', name: 'CourseList', component: () => import('@/views/CourseList.vue'), meta: { title: 'Courses' } },
  { path: '/courses/:id', name: 'CourseDetail', component: () => import('@/views/CourseDetail.vue'), meta: { title: 'Course' } },
  { path: '/learning-paths/:slug', name: 'LearningPathDetail', component: () => import('@/views/LearningPathDetail.vue'), meta: { title: 'Learning path' } },
  { path: '/learn/:id', name: 'LearnView', component: () => import('@/views/LearnView.vue'), meta: { requiresAuth: true, title: 'Learning' } },
  { path: '/modules/:id', name: 'ModuleDetail', component: () => import('@/views/ModuleDetail.vue'), meta: { requiresAuth: true, title: 'Module' } },
  { path: '/lessons/:id', name: 'LessonDetail', component: () => import('@/views/LessonDetail.vue'), meta: { requiresAuth: true, title: 'Lesson' } },
  { path: '/login', name: 'Login', component: () => import('@/views/Login.vue'), meta: { title: 'Sign in' } },
  { path: '/forgot-password', name: 'ForgotPassword', component: () => import('@/views/ForgotPassword.vue'), meta: { title: 'Reset password' } },
  { path: '/reset-password/:uid/:token', name: 'ResetPassword', component: () => import('@/views/ResetPassword.vue'), meta: { title: 'Choose a new password' } },
  { path: '/register', name: 'Register', component: () => import('@/views/Register.vue'), meta: { title: 'Create account' } },
  { path: '/account', name: 'Account', component: () => import('@/views/Account.vue'), meta: { requiresAuth: true, title: 'My account' } },
  { path: '/admin/import-course', name: 'ImportCourse', component: () => import('@/views/ImportCourse.vue'), meta: { requiresRole: 'lecturer', title: 'Import course' } },
  { path: '/admin/generate-course', name: 'GenerateCourse', component: () => import('@/views/GenerateCourse.vue'), meta: { requiresRole: 'lecturer', title: 'Generate course from notes' } },
  { path: '/admin/collections', name: 'ManageCollections', component: () => import('@/views/ManageCollections.vue'), meta: { requiresRole: 'lecturer', title: 'Manage collections' } },
  { path: '/teachers/:username', name: 'TeacherProfile', component: () => import('@/views/TeacherProfile.vue'), meta: { title: 'Teacher profile' } },
  { path: '/verify-certificate', name: 'VerifyCertificate', component: () => import('@/views/VerifyCertificate.vue'), meta: { title: 'Verify certificate' } },
  { path: '/legal', name: 'Legal', component: () => import('@/views/Legal.vue'), meta: { title: 'Legal information' } },
  { path: '/privacy', redirect: { path: '/legal', query: { tab: 'privacy' } } },
  { path: '/terms', redirect: { path: '/legal', query: { tab: 'terms' } } },
  { path: '/cookies', redirect: { path: '/legal', query: { tab: 'cookies' } } },
  { path: '/payment-privacy', redirect: { path: '/legal', query: { tab: 'payment-privacy' } } },
  { path: '/payment-terms', redirect: { path: '/legal', query: { tab: 'payment-terms' } } },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('@/views/NotFound.vue'), meta: { title: 'Page not found' } },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: navigationScroll,
})

// Global navigation guard for auth and role-based access
router.beforeEach(async (to) => {
  const auth = useAuthStore()
  try { await auth.ensureLoaded() } catch (_) { /* Account loading can be retried without clearing credentials. */ }

  if (to.meta && to.meta.requiresAuth && !auth.isAuthenticated) {
    return { path: '/login', query: { next: to.fullPath } }
  }
  if (to.meta && to.meta.requiresRole === 'lecturer' && !auth.isLecturer) {
    return { path: '/' }
  }
  return true
})

router.afterEach((to) => setDocumentTitle(to.meta?.title))

export default router
